// src/lib/supabase/queries/roadmap-queries.ts
/**
 * .tuMetrics {"tu1": 0.84, "tu2": 0.86, "tu3": 5, "notes": "Roadmap DB mappers and CRUD; optional association sync so partial updates don't wipe links.", "tuVersion": "2.5"}
 * .help "Supabase queries and row mappers for roadmaps: fetch with linked sprints/cards/projects, create, update, and delete."
 */
import { supabase } from '../../supabase';
import { Roadmap, SentimentVotes, Milestone } from '../../../types';

// July 2026: types/supabase.ts (generated Database types) is an empty file, so
// the old `Database['public']['Tables']['roadmaps']` lookups never type-checked.
// Hand-written row shapes until codegen is wired up.
interface RoadmapRow {
  id: string;
  name: string;
  description: string;
  status: Roadmap['status'];
  milestones: Milestone[] | null;
  sentiment_votes: unknown;
  github_project_id: string | null;
  team_id: string | null;
  company_id: string | null;
  created_by_id: string | null;
  requested_by_id: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

type RoadmapInsert = Omit<RoadmapRow, 'id' | 'milestones' | 'sentiment_votes'> & {
  milestones: Milestone[];
  sentiment_votes: SentimentVotes | string | null;
};

type RoadmapUpdate = Partial<RoadmapInsert>;

/* --------------------------- helpers & mappers --------------------------- */

export const parseSentimentVotes = (sentimentVotes: any): SentimentVotes => {
  if (!sentimentVotes) return { up: 0, down: 0, user_votes: [] };

  const parsed =
    typeof sentimentVotes === 'string' ? JSON.parse(sentimentVotes) : sentimentVotes;

  if (parsed && typeof parsed === 'object' && 'user_votes' in parsed) {
    return {
      up: Number(parsed.up) || 0,
      down: Number(parsed.down) || 0,
      user_votes: Array.isArray(parsed.user_votes) ? parsed.user_votes : [],
    };
  } else if (parsed && typeof parsed === 'object' && 'votedBy' in parsed) {
    return {
      up: Number(parsed.up) || 0,
      down: Number(parsed.down) || 0,
      user_votes: Array.isArray(parsed.votedBy)
        ? parsed.votedBy.map((userId: string) => ({ user_id: userId, type: 'up' as const }))
        : [],
    };
  }

  return { up: 0, down: 0, user_votes: [] };
};

export const mapRoadmapFromDB = (row: RoadmapRow): Roadmap => ({
  id: row.id,
  name: row.name,
  description: row.description,
  status: row.status,
  milestones: row.milestones ? JSON.parse(JSON.stringify(row.milestones)) : [],
  sprints: [],
  cardIds: [],
  sentimentVotes: parseSentimentVotes(row.sentiment_votes),
  githubProject: row.github_project_id ? JSON.parse(row.github_project_id) : undefined,
  teamId: row.team_id || undefined,
  companyId: row.company_id || undefined,
  createdById: row.created_by_id || undefined,
  requestedById: row.requested_by_id || undefined,
  createdAt: row.created_at || undefined,
  updatedAt: row.updated_at || undefined,
});

export const mapRoadmapToDB = (
  roadmap: Omit<Roadmap, 'id' | 'sprints' | 'cardIds'>
): RoadmapInsert => ({
  name: roadmap.name,
  description: roadmap.description,
  status: roadmap.status,
  milestones: roadmap.milestones ?? [],
  sentiment_votes: roadmap.sentimentVotes ?? { up: 0, down: 0, user_votes: [] },
  github_project_id: roadmap.githubProject ? JSON.stringify(roadmap.githubProject) : null,
  team_id: roadmap.teamId || null,
  company_id: roadmap.companyId || null,
  created_by_id: roadmap.createdById || null,
  requested_by_id: roadmap.requestedById || null,
});

export const mapRoadmapUpdatesToDB = (updates: Partial<Roadmap>): RoadmapUpdate => {
  const dbUpdates: RoadmapUpdate = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.milestones !== undefined) dbUpdates.milestones = updates.milestones ?? [];
  if (updates.sentimentVotes !== undefined) dbUpdates.sentiment_votes = updates.sentimentVotes;
  if (updates.githubProject !== undefined)
    dbUpdates.github_project_id = updates.githubProject ? JSON.stringify(updates.githubProject) : null;
  if (updates.teamId !== undefined) dbUpdates.team_id = updates.teamId || null;
  if (updates.companyId !== undefined) dbUpdates.company_id = updates.companyId || null;
  if (updates.createdById !== undefined) dbUpdates.created_by_id = updates.createdById || null;
  if (updates.requestedById !== undefined) dbUpdates.requested_by_id = updates.requestedById || null;
  return dbUpdates;
};

/* --------------------------- supabase operations --------------------------- */

export const fetchRoadmaps = async (companyId: string, selectedTeamId: string | null) => {
  let query = supabase.from('roadmaps').select('*').eq('company_id', companyId);

  if (selectedTeamId !== null) {
    query = query.eq('team_id', selectedTeamId);
  }

  query = query.order('created_at', { ascending: false });

  const { data: roadmapsData, error: roadmapsError } = await query;
  if (roadmapsError) throw roadmapsError;

  const { data: sprintsData, error: sprintsError } = await supabase
    .from('sprints')
    .select('id, roadmap_id')
    .eq('company_id', companyId);
  if (sprintsError) throw sprintsError;

  const { data: cardsData, error: cardsError } = await supabase
    .from('cards')
    .select('id, roadmap_id')
    .eq('company_id', companyId);
  if (cardsError) throw cardsError;

  const { data: projectsData, error: projectsError } = await supabase
    .from('projects')
    .select('id, roadmap_id')
    .eq('company_id', companyId);
  if (projectsError) throw projectsError;

  const sprintsByRoadmap = (sprintsData || []).reduce((acc, sprint) => {
    if (sprint.roadmap_id) {
      if (!acc[sprint.roadmap_id]) acc[sprint.roadmap_id] = [];
      acc[sprint.roadmap_id].push(sprint.id);
    }
    return acc;
  }, {} as Record<string, string[]>);

  const cardsByRoadmap = (cardsData || []).reduce((acc, card) => {
    if (card.roadmap_id) {
      if (!acc[card.roadmap_id]) acc[card.roadmap_id] = [];
      acc[card.roadmap_id].push(card.id);
    }
    return acc;
  }, {} as Record<string, string[]>);

  const projectsByRoadmap = (projectsData || []).reduce((acc, project) => {
    if (project.roadmap_id) {
      if (!acc[project.roadmap_id]) acc[project.roadmap_id] = [];
      acc[project.roadmap_id].push(project.id);
    }
    return acc;
  }, {} as Record<string, string[]>);

  const roadmaps = (roadmapsData || []).map((roadmap) => ({
    ...mapRoadmapFromDB(roadmap),
    sprints: sprintsByRoadmap[roadmap.id] || [],
    cardIds: cardsByRoadmap[roadmap.id] || [],
    projectIds: projectsByRoadmap[roadmap.id] || [],
  }));

  return roadmaps;
};

/** When provided, sync that association set. Omitted keys are left untouched. */
export type RoadmapAssociationSync = {
  sprintIds?: string[];
  cardIds?: string[];
  projectIds?: string[];
};

/** Associations applied on roadmap create (all optional; empty = link nothing). */
export type RoadmapCreateAssociations = {
  sprintIds?: string[];
  cardIds?: string[];
  projectIds?: string[];
  featureIds?: string[];
  planIds?: string[];
};

export const insertRoadmap = async (
  roadmapData: RoadmapInsert,
  associations: RoadmapCreateAssociations = {}
) => {
  const {
    sprintIds = [],
    cardIds = [],
    projectIds = [],
    featureIds = [],
    planIds = [],
  } = associations;

  const { data: newRoadmap, error } = await supabase
    .from('roadmaps')
    .insert(roadmapData)
    .select('*')
    .single();
  if (error) throw error;

  // Link by id only (RLS still scopes writes). Requiring company_id match silently
  // skipped rows when company_id was null/mismatched — looked like "some types don't save".
  if (projectIds.length > 0) {
    const { data: linked, error: projectsError } = await supabase
      .from('projects')
      .update({ roadmap_id: newRoadmap.id })
      .in('id', projectIds)
      .select('id');
    if (projectsError) throw projectsError;
    if ((linked?.length ?? 0) !== projectIds.length) {
      console.warn('[insertRoadmap] Not all projects linked', {
        requested: projectIds.length,
        linked: linked?.length ?? 0,
      });
    }
  }

  if (sprintIds.length > 0) {
    const { data: linked, error: sprintsError } = await supabase
      .from('sprints')
      .update({ roadmap_id: newRoadmap.id })
      .in('id', sprintIds)
      .select('id');
    if (sprintsError) throw sprintsError;
    if ((linked?.length ?? 0) !== sprintIds.length) {
      console.warn('[insertRoadmap] Not all sprints linked', {
        requested: sprintIds.length,
        linked: linked?.length ?? 0,
      });
    }
  }

  if (cardIds.length > 0) {
    const { data: linked, error: cardsError } = await supabase
      .from('cards')
      .update({ roadmap_id: newRoadmap.id })
      .in('id', cardIds)
      .select('id');
    if (cardsError) throw cardsError;
    if ((linked?.length ?? 0) !== cardIds.length) {
      console.warn('[insertRoadmap] Not all cards linked', {
        requested: cardIds.length,
        linked: linked?.length ?? 0,
      });
    }
  }

  if (featureIds.length > 0) {
    const { data: linked, error: featuresError } = await supabase
      .from('requirements')
      .update({ roadmap_id: newRoadmap.id })
      .in('id', featureIds)
      .select('id');
    if (featuresError) throw featuresError;
    if ((linked?.length ?? 0) !== featureIds.length) {
      console.warn('[insertRoadmap] Not all features linked', {
        requested: featureIds.length,
        linked: linked?.length ?? 0,
      });
    }
  }

  if (planIds.length > 0) {
    const { data: plans, error: plansFetchError } = await supabase
      .from('plans')
      .select('id, plan_settings')
      .in('id', planIds);
    if (plansFetchError) throw plansFetchError;

    for (const plan of plans || []) {
      const raw = plan.plan_settings;
      const settings =
        typeof raw === 'string' ? JSON.parse(raw || '{}') : { ...(raw || {}) };
      const currentIds: string[] = Array.isArray(settings.roadmap_ids)
        ? settings.roadmap_ids
        : settings.roadmap_id
          ? [settings.roadmap_id]
          : [];
      const nextIds = [
        ...currentIds.filter((id: string) => id !== newRoadmap.id),
        newRoadmap.id,
      ];
      const { error: planUpdateError } = await supabase
        .from('plans')
        .update({
          plan_settings: {
            ...settings,
            roadmap_ids: nextIds,
            roadmap_id: null,
          },
        })
        .eq('id', plan.id);
      if (planUpdateError) throw planUpdateError;
    }
  }

  return newRoadmap;
};

export const updateRoadmapById = async (
  roadmapId: string,
  updates: Partial<Roadmap>,
  associations?: RoadmapAssociationSync
) => {
  const dbUpdates = mapRoadmapUpdatesToDB(updates);

  const { data: updatedRoadmapRow, error } = await supabase
    .from('roadmaps')
    .update(dbUpdates)
    .eq('id', roadmapId)
    .select('*')
    .single();
  if (error) throw error;

  // Only sync association types that were explicitly provided. Partial updates
  // (name, milestones, vote, sprints-only) must not treat omitted arrays as [].
  if (associations?.projectIds !== undefined) {
    const projectIds = associations.projectIds;
    const { data: currentProjects, error: currentProjectsError } = await supabase
      .from('projects')
      .select('id')
      .eq('roadmap_id', roadmapId);
    if (currentProjectsError) throw currentProjectsError;

    const currentProjectIds = new Set((currentProjects || []).map((p) => p.id));
    const projectsToUnlink = [...currentProjectIds].filter((id) => !projectIds.includes(id));
    if (projectsToUnlink.length > 0) {
      const { error: unlinkError } = await supabase
        .from('projects')
        .update({ roadmap_id: null })
        .in('id', projectsToUnlink);
      if (unlinkError) throw unlinkError;
    }

    const projectsToLink = projectIds.filter((id) => !currentProjectIds.has(id));
    if (projectsToLink.length > 0) {
      const { error: linkError } = await supabase
        .from('projects')
        .update({ roadmap_id: roadmapId })
        .in('id', projectsToLink);
      if (linkError) throw linkError;
    }
  }

  if (associations?.sprintIds !== undefined) {
    const sprintIds = associations.sprintIds;
    const { data: currentSprints, error: currentSprintsError } = await supabase
      .from('sprints')
      .select('id')
      .eq('roadmap_id', roadmapId);
    if (currentSprintsError) throw currentSprintsError;

    const currentSprintIds = new Set((currentSprints || []).map((s) => s.id));
    const sprintsToUnlink = [...currentSprintIds].filter((id) => !sprintIds.includes(id));
    if (sprintsToUnlink.length > 0) {
      const { error: unlinkError } = await supabase
        .from('sprints')
        .update({ roadmap_id: null })
        .in('id', sprintsToUnlink);
      if (unlinkError) throw unlinkError;
    }

    const sprintsToLink = sprintIds.filter((id) => !currentSprintIds.has(id));
    if (sprintsToLink.length > 0) {
      const { error: linkError } = await supabase
        .from('sprints')
        .update({ roadmap_id: roadmapId })
        .in('id', sprintsToLink);
      if (linkError) throw linkError;
    }
  }

  if (associations?.cardIds !== undefined) {
    const cardIds = associations.cardIds;
    const { data: currentCards, error: currentCardsError } = await supabase
      .from('cards')
      .select('id')
      .eq('roadmap_id', roadmapId);
    if (currentCardsError) throw currentCardsError;

    const currentCardIds = new Set((currentCards || []).map((c) => c.id));
    const cardsToUnlink = [...currentCardIds].filter((id) => !cardIds.includes(id));
    if (cardsToUnlink.length > 0) {
      const { error: unlinkCardsError } = await supabase
        .from('cards')
        .update({ roadmap_id: null })
        .in('id', cardsToUnlink);
      if (unlinkCardsError) throw unlinkCardsError;
    }

    const cardsToLink = cardIds.filter((id) => !currentCardIds.has(id));
    if (cardsToLink.length > 0) {
      const { error: linkCardsError } = await supabase
        .from('cards')
        .update({ roadmap_id: roadmapId })
        .in('id', cardsToLink);
      if (linkCardsError) throw linkCardsError;
    }
  }

  return updatedRoadmapRow;
};

export const deleteRoadmapById = async (roadmapId: string) => {
  // Clear project associations first
  const { error: clearProjectsError } = await supabase
    .from('projects')
    .update({ roadmap_id: null })
    .eq('roadmap_id', roadmapId);
  if (clearProjectsError) throw clearProjectsError;

  // Clear card associations
  const { error: clearCardsError } = await supabase
    .from('cards')
    .update({ roadmap_id: null })
    .eq('roadmap_id', roadmapId);
  if (clearCardsError) throw clearCardsError;

  // Clear sprint associations
  const { error: clearSprintsError } = await supabase
    .from('sprints')
    .update({ roadmap_id: null })
    .eq('roadmap_id', roadmapId);
  if (clearSprintsError) throw clearSprintsError;

  const { error } = await supabase.from('roadmaps').delete().eq('id', roadmapId);
  if (error) throw error;
  return true;
};